import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: 'student' | 'supervisor' | 'ojt_coordinator';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { session, user, loading, userType } = useAuth();

  console.log('ProtectedRoute Debug:', {
    loading,
    sessionExists: !!session,
    role: requiredRole,
    currentUserType: userType,
    hasUser: !!user,
    userRole: user && 'user_role' in user ? (user as any).user_role : 'N/A'
  });

  // Log loading state for debugging
  useEffect(() => {
    if (loading) {
      console.log('Loading auth state...', { 
        hasSession: !!session, 
        hasUser: !!user,
        timestamp: new Date().toISOString()
      });
    }
  }, [loading, session, user]);

  // Show loading state if we're still loading
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        <div>
          <div>Loading your profile...</div>
          <div style={{ fontSize: '0.8rem', marginTop: '10px', color: '#666' }}>
            Please wait a moment...
          </div>
        </div>
      </div>
    );
  }

  // If we have a session but no user yet, show loading (profile might be loading)
  if (session && !user && !loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        <div>
          <div>Loading your profile...</div>
          <div style={{ fontSize: '0.8rem', marginTop: '10px', color: '#666' }}>
            Fetching your data...
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated — be tolerant to placeholder session during refresh
  if (!session && !loading) {
    console.log('No active session and not loading, redirecting to login');

    console.log('No active session found, redirecting to login');
    return <Navigate to={requiredRole === 'student' ? '/' : '/supervisor'} replace />;
  }

  // Check if user has a profile and correct role
  if (!user && !loading) {
    console.log('No user profile and not loading, redirecting to login');

    console.log('No user profile found, redirecting to login');
    return <Navigate to={requiredRole === 'student' ? '/' : '/supervisor'} replace />;
  }

  // Validate user type matches required role
  if (requiredRole === 'student' && userType !== 'student') {
    console.log('Not a student, redirecting to student login');
    return <Navigate to="/" replace />;
  }

  if (requiredRole === 'supervisor') {
    const isSupervisorOrCoordinator = userType === 'supervisor' || userType === 'ojt_coordinator';
    if (!isSupervisorOrCoordinator) {
      console.log('Not a supervisor/coordinator, redirecting to supervisor login');
      return <Navigate to="/supervisor" replace />;
    }
  }

  if (requiredRole === 'ojt_coordinator') {
    // Check if user is OJT coordinator or has OJT coordinator role in profile
    const isOJTCoordinator = userType === 'ojt_coordinator' || 
                            (user && 'user_role' in user && (user as any).user_role === 'ojt_coordinator');
    
    if (!isOJTCoordinator) {
      console.log('Not an OJT coordinator, redirecting to supervisor login');
      console.log('User details:', { userType, userRole: (user as any)?.user_role, hasUser: !!user });
      return <Navigate to="/supervisor" replace />;
    } else {
      console.log('OJT coordinator access granted');
    }
  }

  // All checks passed
  console.log('Authentication successful, rendering protected content');
  return <>{children}</>;
};
